import { declareParams, JsonApiRequestError } from '@farfetched/core';
import { zodContract } from '@farfetched/zod';
import { combine, createStore, Store } from 'effector';
import { applyTimeZone, RegistryParams, RegistryQuery } from 'shared/lib';
import { createAuthQuery } from '../auth-http-client';
import { AccountTransaction, accountTransactionSchema, createPaginatedSchema } from '../types';

export type OriginalAccountTransactionRegistryParams = {
	page?: number;
	limit?: number;
	sortField?: string | null;
	sortDirection?: string;
	timeZone?: number;
	dateTo?: string;
	dateFrom?: string;
	accountIds?: string[];
	currencies?: string[];
	types?: string[];
};

export type AccountTransactionRegistryFilters = {
	accountIds: number[];
	currencies: string[];
	types: string[];
	dateFrom: string;
	dateTo: string;
	timeZone: number;
};

export type AccountTransactionRegistryParams = RegistryParams<AccountTransactionRegistryFilters>;

export type AccountTransactionRegistryQuery = RegistryQuery<
	AccountTransaction,
	AccountTransactionRegistryFilters,
	JsonApiRequestError
>;

export const createAccountTransactionRegistryQuery = (
	$accountId?: Store<number | null>,
): AccountTransactionRegistryQuery => {
	const query = createAuthQuery({
		params: declareParams<AccountTransactionRegistryParams>(),
		request: {
			method: 'GET',
			url: '/transaction/account',
			query: {
				source: combine([$accountId ?? createStore<number | null>(null)]),
				fn: (params, [accountId]): OriginalAccountTransactionRegistryParams => ({
					page: params.page,
					limit: params.limit,
					sortField: params.sortField ?? undefined,
					sortDirection: params.sortDirection,
					timeZone: params.filters.timeZone,
					dateTo: params.filters.dateTo || undefined,
					dateFrom: params.filters.dateFrom || undefined,
					accountIds: [
						...(accountId ? [String(accountId)] : []),
						...params.filters.accountIds.map(String),
					],
					currencies:
						params.filters.currencies.length === 0 ? undefined : params.filters.currencies,
					types: params.filters.types.length === 0 ? undefined : params.filters.types,
				}),
			},
		},
		response: {
			contract: zodContract(createPaginatedSchema(accountTransactionSchema)),
			mapData: ({ result, params }) => ({
				...result,
				rows: result.rows.map<AccountTransaction>((row) => ({
					...row,
					date: applyTimeZone(row.date, params.filters.timeZone),
				})),
			}),
		},
	});

	return query;
};
